import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Create axios instance with auth interceptor
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Unified Task interface that works for both ID verification and email tasks
export interface UnifiedTask {
  workflow?: import('./cafAutomation').RunSummary | null;
  id: string;
  type: 'id_verification' | 'email_lender_response' | 'email_lender_query' | 'email_client' | 'email_other' | 'manual' | 'post';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt?: string;
  
  // Common fields
  clientId?: string;
  clientName?: string;
  clientEmail?: string;
  displayType?: string;
  priority?: 'high' | 'medium' | 'low';
  
  // Due date and scheduling fields
  dueDate?: string;
  isOverdue?: boolean;
  daysUntilDue?: number;
  
  // Manual task fields
  subject?: string;
  description?: string;
  
  // ID Verification specific fields
  clientDOB?: string;
  processedAt?: string;
  processedBy?: string;
  attachments?: Array<{
    filename: string;
    blobName?: string;
    type?: string;
    sizeBytes?: number;
    secureUrl?: string;
    isInline?: boolean;
  }>;
  associatedClaimCount?: number;
  aiProcessed?: boolean;
  aiNotes?: string;
  aiApproved?: boolean;
  rejectionReason?: string;
  emailMetadata?: {
    from: string;
    subject: string;
    receivedAt: string;
  };
  
  // Email Task specific fields
  emailContent?: {
    from: string;
    to: string;
    subject: string;
    body: string;
    receivedDate: string;
    attachments?: any[];
    claimRef?: string;
    documentId?: string;
    documentUrl?: string;
    blobPath?: string;
    importedAt?: string;
    candidateClaims?: { id: string; reference?: string }[];
    /** Outlook web deep link (ticket #378) -- populated by the CAF/DSAR
     * inbox intake. Present alongside `isEncrypted` so staff can open
     * Purview-encrypted mail where the licensed mailbox CAN decrypt it. */
    webLink?: string;
    /** True when this email is a Microsoft Purview "protected message"
     * wrapper -- Graph cannot decrypt cross-tenant Purview content, so the
     * body is a useless generic shell. See `webLink`. */
    isEncrypted?: boolean;
  };
  /** Post lane's Claim Ref, sourced from the real (undropped) post_claim_ref
   * column -- a fallback for emailContent?.claimRef that survives the
   * Tasks Rebuild POST-SOAK cleanup migration nulling unified_tasks_view's
   * emailContent projection. Only populated for /ui/tasks/list rows;
   * prefer `postClaimRef ?? emailContent?.claimRef` when reading Claim Ref. */
  postClaimRef?: string | null;
  emailType?: 'lender_response' | 'lender_query' | 'client_email' | 'other' | 'follow_up';
  /** Populated when a follow-up task is resolved (e.g. 'client_responded'). */
  followUpResolvedReason?: string;
  /** ID of the email task that resolved the follow-up (client's reply). */
  followUpResponseTaskId?: string;
  email_source?: string;
  assignedClaimId?: string;
  assignedBy?: string;
  assignedAt?: string;
  /** Staff assignment restoration (fast-follow) -- name of the assigned
   * team member (see app/routes/team_tasks.py's TEAM_MEMBERS), or absent/
   * null when unassigned. Sourced from task_lane_service's
   * LANE_TASK_DETAIL_*_SELECT (always null on an archived task). */
  assignedToTeamMember?: string | null;
  completedBy?: string;
  completedAt?: string;
  claudeAnalysis?: {
    attempted?: boolean;
    confidence?: number;
    category?: string;
    reasoning?: string;
    reason?: string;
    snippet?: string;
    source?: string;
    evaluatedAt?: string;
    extractedData?: any;
  };

  // AI Classification fields (CAF Backlog Processor)
  aiCategory?: string;
  aiConfidence?: number;
  aiReasoning?: string;
  aiAction?: 'AUTO_ARCHIVE' | 'AUTO_ASSIGN_COMPLETE' | 'AUTO_ASSIGN_ONLY' | 'AUTO_ASSIGN_SEND_LOA' | 'AUTO_ASSIGN_MULTI_REP' | 'AUTO_ASSIGN_CANCEL_CONFIRM' | 'MANUAL_REVIEW';
  aiClaimReference?: string;
  aiClientName?: string;
  aiLenderName?: string;
  aiAgreementNumber?: string;
  aiProcessedAt?: string;
  /** Claim refs quoted in the subject (GET /ui/lanes/task/<id> only --
   * that lookup never reads the body; see TaskContent.referencedClaimRefs).
   * Wrong-claim attach guard, 2026-09-28. */
  subjectClaimRefs?: string[];

  // RAG / human-led precedent fields (PR 5)
  // ai_precedents_used: list of corpus entry IDs the LLM was shown
  aiPrecedentsUsed?: string[] | null;
  // Hydrated corpus entries — server-side join from caf_corpus_entries
  aiPrecedents?: Array<{
    id: string;
    sender?: string | null;
    subject_snapshot?: string | null;
    correct_action?: string | null;
    note?: string | null;
  }> | null;
  // Staff classification override audit (live-override path)
  overriddenAction?: string | null;
  overriddenBy?: string | null;
  overriddenAt?: string | null;

  // Original email received date (PR 5 side job — read-only display)
  emailReceivedAt?: string | null;

  // Archive fields
  archivedAt?: string;
  archivedBy?: string;
  archiveReason?: string;
}

export interface TaskListResponse {
  tasks: UnifiedTask[];
  // null when skipCount was requested (page navigation) — the caller reuses
  // the totals it already holds from the first page load of the same filter.
  totalCount: number | null;
  currentPage: number;
  totalPages: number | null;
  pageSize: number;
}

export interface TaskStats {
  summary: {
    totalPending: number;
    totalInProgress: number;
    totalCompleted: number;
    completedToday: number;
    completedThisWeek: number;
  };
  byType: {
    id_verification: {
      pending: number;
      approved: number;
      rejected: number;
    };
    email_tasks: {
      pending: number;
      in_progress: number;
      completed: number;
      byEmailType: Record<string, number>;
    };
  };
}

export interface SenderGroup {
  sender: string;
  displayName: string | null;
  clientName: string | null;
  taskCount: number;
  oldestReceived: string | null;
  newestReceived: string | null;
}

export interface SenderGroupsResponse {
  groups: SenderGroup[];
  totalCount: number | null;
  currentPage: number;
  pageSize: number;
}

// Unified Tasks API
export const unifiedTasksApi = {
  // List tasks with unified filtering and sorting
  async listTasks(params: {
    status?: string;
    taskType?: string;
    startDate?: string;
    endDate?: string;
    searchTerm?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    aiAction?: string;
    teamMember?: string;
    senderFilter?: string;
    receivedFrom?: string;
    senderEmail?: string;
    skipCount?: boolean;
    countOnly?: boolean;
  }): Promise<TaskListResponse> {
    const queryParams = new URLSearchParams();

    if (params.skipCount) queryParams.append('skipCount', 'true');
    if (params.countOnly) queryParams.append('countOnly', 'true');
    if (params.status) queryParams.append('status', params.status);
    if (params.taskType) queryParams.append('taskType', params.taskType);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.aiAction) {
      queryParams.append('aiAction', params.aiAction);
    }
    if (params.teamMember) queryParams.append('teamMember', params.teamMember);
    if (params.senderFilter && params.senderFilter !== 'all') {
      queryParams.append('senderFilter', params.senderFilter);
    }
    if (params.receivedFrom) queryParams.append('receivedFrom', params.receivedFrom);
    if (params.senderEmail) queryParams.append('senderEmail', params.senderEmail);

    const url = `/ui/tasks/list?${queryParams}`;
    const response = await axiosInstance.get<TaskListResponse>(url);
    return response.data;
  },

  // Get sender groups
  async getSenderGroups(params: {
    searchTerm?: string;
    sortBy?: 'oldestReceived' | 'taskCount';
    sortOrder?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    skipCount?: boolean;
  }): Promise<SenderGroupsResponse> {
    const queryParams = new URLSearchParams();
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.skipCount) queryParams.append('skipCount', 'true');
    const response = await axiosInstance.get<SenderGroupsResponse>(
      `/ui/tasks/sender-groups?${queryParams}`);
    return response.data;
  },

  // Get a specific task
  async getTask(taskId: string): Promise<UnifiedTask> {
    const response = await axiosInstance.get<UnifiedTask>(`/ui/tasks/${taskId}`);
    return response.data;
  },
  
  // Approve ID verification task
  async approveTask(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `/ui/tasks/${taskId}/approve`
    );
    return response.data;
  },
  
  // Reject ID verification task
  async rejectTask(taskId: string, reason: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `/ui/tasks/${taskId}/reject`,
      { reason }
    );
    return response.data;
  },
  
  // Mark ID verification task as duplicate
  async markAsDuplicate(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `/ui/tasks/${taskId}/duplicate`
    );
    return response.data;
  },
  
  // Assign email task to claim
  async assignTask(taskId: string, claimId: string, emailType?: string): Promise<UnifiedTask> {
    const response = await axiosInstance.post<UnifiedTask>(
      `/ui/tasks/${taskId}/assign`,
      { claimId, emailType }
    );
    return response.data;
  },

  // Reassign task to a different claim
  async reassignTask(taskId: string, newClaimId: string, reason?: string): Promise<UnifiedTask> {
    const response = await axiosInstance.post<UnifiedTask>(
      `/ui/tasks/${taskId}/reassign`,
      { claimId: newClaimId, reason }
    );
    return response.data;
  },

  // Remove a mistaken claim link without deleting or reopening the task
  async unassignClaim(taskId: string, claimId: string): Promise<UnifiedTask> {
    const response = await axiosInstance.post<UnifiedTask>(
      `/ui/tasks/${taskId}/unassign-claim`,
      { claimId }
    );
    return response.data;
  },

  // Complete email task
  async completeTask(taskId: string, notes?: string): Promise<UnifiedTask> {
    const response = await axiosInstance.post<UnifiedTask>(
      `/ui/tasks/${taskId}/complete`,
      { notes }
    );
    return response.data;
  },

  // Cancel the claim bound to a special cancellation-recovery task
  async cancelClaimFromTask(
    taskId: string,
    opts: { claimId?: string; paymentDecision: 'request' | 'skip' },
  ): Promise<{ success: boolean; cancelledClaimIds: string[]; requiresPayment: boolean; paymentDetails: any }> {
    const response = await axiosInstance.post<{ success: boolean; cancelledClaimIds: string[]; requiresPayment: boolean; paymentDetails: any }>(
      `/ui/tasks/${taskId}/cancel-claim`,
      { claimId: opts.claimId, paymentDecision: opts.paymentDecision },
    );
    return response.data;
  },

  // Archive email task (one-way operation for tasks that cannot be assigned)
  async archiveTask(taskId: string, reason: string): Promise<{
    message: string;
    task_id: string;
    archived_by: string;
    archived_at: string;
    reason: string;
  }> {
    const response = await axiosInstance.post<{
      message: string;
      task_id: string;
      archived_by: string;
      archived_at: string;
      reason: string;
    }>(
      `/ui/email-tasks/${taskId}/archive`,
      { reason }
    );
    return response.data;
  },

  // Reply to email task
  async replyToTask(taskId: string, params: {
    subject: string;
    body: string;
    markComplete?: boolean;
  }): Promise<{ success: boolean; message: string; replyInfo?: any }> {
    const response = await axiosInstance.post<{ success: boolean; message: string; replyInfo?: any }>(
      `/ui/tasks/${taskId}/reply`,
      params
    );
    return response.data;
  },
  
  // Get task statistics
  async getStats(): Promise<TaskStats> {
    const response = await axiosInstance.get<TaskStats>('/ui/tasks/stats');
    return response.data;
  },
  
  // Delete task (admin only)
  async deleteTask(taskId: string, source?: string): Promise<{ success: boolean; message: string }> {
    // Add source parameter to prevent cross-table deletions
    const params = source ? `?source=${source}` : '';
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `/ui/tasks/${taskId}${params}`
    );
    return response.data;
  },
  
  // Export tasks
  async startExport(params: {
    taskType?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    format?: 'excel' | 'csv';
  }): Promise<{ exportId: string; message: string }> {
    const response = await axiosInstance.post<{ exportId: string; message: string }>(
      '/ui/tasks/export',
      params
    );
    return response.data;
  },
  
  // Get export progress
  async getExportProgress(exportId: string): Promise<{
    status: 'pending' | 'in_progress' | 'completed' | 'error';
    progress: number;
    totalRecords?: number;
    processedRecords?: number;
    downloadUrl?: string;
    error?: string;
  }> {
    const response = await axiosInstance.get<{
      status: 'pending' | 'in_progress' | 'completed' | 'error';
      progress: number;
      totalRecords?: number;
      processedRecords?: number;
      downloadUrl?: string;
      error?: string;
    }>(`/ui/tasks/export/${exportId}/progress`);
    return response.data;
  },
  
  // Import tasks from CSV
  async importTasks(file: File, autoComplete: boolean = true): Promise<{
    success: boolean | number;
    message: string;
    imported?: number;
    leftOpenPosts?: Array<{ row: number; task_id: string; reason: string }>;
    skippedTerminalPosts?: Array<{ row: number; task_id: string; status: string; reason: string }>;
    errors?: Array<{ row: number; error: string }>;
    action?: string;
    autoComplete?: boolean;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('autoComplete', autoComplete.toString());
    
    const response = await axiosInstance.post<{
      success: boolean | number;
      message: string;
      imported?: number;
      leftOpenPosts?: Array<{ row: number; task_id: string; reason: string }>;
      skippedTerminalPosts?: Array<{ row: number; task_id: string; status: string; reason: string }>;
      errors?: Array<{ row: number; error: string }>;
      action?: string;
      autoComplete?: boolean;
    }>('/ui/tasks/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // Create a new task
  async createTask(params: {
    type: string;
    claimId?: string;
    subject?: string;
    description?: string;
    dueDate?: string;
    priority?: 'high' | 'medium' | 'low';
  }): Promise<UnifiedTask> {
    const response = await axiosInstance.post<UnifiedTask>('/ui/tasks/create', params);
    return response.data;
  },
  
  // Update task due date
  async updateDueDate(taskId: string, dueDate: string | null): Promise<UnifiedTask> {
    const response = await axiosInstance.put<UnifiedTask>(
      `/ui/tasks/${taskId}/due-date`,
      { dueDate }
    );
    return response.data;
  },
  
  // Update task type/category
  async updateTaskType(taskId: string, type: string): Promise<UnifiedTask> {
    const response = await axiosInstance.put<UnifiedTask>(
      `/ui/tasks/${taskId}/type`,
      { type }
    );
    return response.data;
  },
  
  // Get tasks for a claim
  async getTasksForClaim(claimId: string): Promise<UnifiedTask[]> {
    const response = await axiosInstance.get<UnifiedTask[]>(`/ui/tasks/claim/${claimId}`);
    return response.data;
  },
  
  // Legacy photo ID specific methods for backward compatibility
  async resetAiProcessing(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `/ui/photo-id/tasks/${taskId}/reset-ai`
    );
    return response.data;
  },

  // List archived tasks (admin only)
  async listArchivedTasks(params: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<TaskListResponse> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await axiosInstance.get<TaskListResponse>(
      `/ui/admin/archived-tasks?${queryParams}`
    );
    return response.data;
  },

  // Unarchive task (admin only)
  async unarchiveTask(taskId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      `/ui/admin/archived-tasks/${taskId}/unarchive`
    );
    return response.data;
  }
};

// Export for backward compatibility
export const approveVerification = (taskId: string) => unifiedTasksApi.approveTask(taskId);
export const rejectVerification = (taskId: string, reason: string) => unifiedTasksApi.rejectTask(taskId, reason);
